import { Box, Input, ListItem, SectionHeader } from "arkh";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import { useNavigationHeader } from "../../../common/navigationHeaderOptions";
import { UserLoggedInNavigatorScreenProps } from "../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { useLazyLoadQuery, graphql, useMutation } from "react-relay";

import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { PlaceSearchField, SelectedPlace } from "./PlaceSearchField";

import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import { WorkspaceSelector } from "../WorkspaceSelector/WorkspaceSelector";

import { OrgStoreAdminQuery } from "./__generated__/OrgStoreAdminQuery.graphql";

import { OrgStoreAdminUpdateStoreMutation } from "./__generated__/OrgStoreAdminUpdateStoreMutation.graphql";
import { TimezoneSelector } from "./TimezoneSelector";
import { LanguageSelector } from "./LanguageSelector";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { OrgStoreAdminCopyStoreMutation } from "./__generated__/OrgStoreAdminCopyStoreMutation.graphql";
import { OrgStoreAdminArchiveStoreMutation } from "./__generated__/OrgStoreAdminArchiveStoreMutation.graphql";
import { useAlert } from "@/src/common/Alert";

function OrgStoreAdminC(
  props: UserLoggedInNavigatorScreenProps<"OrgStoreAdmin">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fk) => (
        <OrgStoreAdminC_
          {...props}
          fetchKey={fk}
          key={props.route.params.storeId}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function OrgStoreAdminC_(
  props: UserLoggedInNavigatorScreenProps<"OrgStoreAdmin"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<OrgStoreAdminQuery>(
    graphql`
      query OrgStoreAdminQuery($storeId: ID!) {
        viewer {
          workspaces {
            ...WorkspaceSelector_workspaces
          }
        }
        store(id: $storeId) {
          id
          name
          workspaceOwner {
            id
            name
          }
          locale
          timezone
          externalId
          extraAdminFields {
            key
            label
            description
            type
            value
          }
          address
        }
      }
    `,
    {
      storeId: props.route.params.storeId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const [updateStore, updateInFlight] =
    useMutation<OrgStoreAdminUpdateStoreMutation>(graphql`
      mutation OrgStoreAdminUpdateStoreMutation(
        $input: StoreOrgUpdateInput!
        $storeId: ID!
      ) {
        store(id: $storeId) {
          updateByOrg(input: $input) {
            id
            name
            timezone
            locale
            externalId
            address
            workspaceOwner {
              id
            }
            extraAdminFields {
              key
              label
              description
              type
              value
            }
          }
        }
      }
    `);

  const intl_tokens = useIntlTokens();
  const [name, setName] = useState<string>(data.store.name ?? "");
  const [parentWorkspaceId, setParentWorkspaceId] = useState<string>(
    data.store.workspaceOwner?.id ?? ""
  );

  const parentWorkspaceIdHasChanged = parentWorkspaceId
    ? parentWorkspaceId !== data.store.workspaceOwner?.id
    : false;

  const wId = parentWorkspaceId ?? data.store.workspaceOwner?.id;

  const [timezone, setTimezone] = useState<string>(
    data.store.timezone ?? "Europe/Stockholm"
  );
  const [externalId, setExternalId] = useState<string>(
    data.store.externalId ?? ""
  );
  const [language, setLanguage] = useState<string>(data.store.locale ?? "en");
  const [selectedPlace, setSelectedPlace] = useState<SelectedPlace | null>(
    data.store.address ? { placeId: "", address: data.store.address } : null
  );

  const initialExtraFields = useMemo(() => {
    const map: Record<string, string> = {};
    for (const field of data.store.extraAdminFields) {
      map[field.key] = field.value ?? "";
    }
    return map;
  }, [data.store.extraAdminFields]);

  const [extraFields, setExtraFields] =
    useState<Record<string, string>>(initialExtraFields);

  const extraFieldsHasChanged = useMemo(() => {
    for (const key of Object.keys(extraFields)) {
      if (extraFields[key] !== initialExtraFields[key]) return true;
    }
    return false;
  }, [extraFields, initialExtraFields]);

  const nameHasChanged = name ? name !== data.store.name : false;
  const timezoneHasChanged = timezone
    ? timezone !== data.store.timezone
    : false;
  const languageHasChanged = language ? language !== data.store.locale : false;
  const externalIdHasChanged = externalId
    ? externalId !== data.store.externalId
    : false;
  const addressHasChanged =
    (selectedPlace?.address ?? null) !== (data.store.address ?? null);

  const isDirty =
    parentWorkspaceIdHasChanged ||
    nameHasChanged ||
    timezoneHasChanged ||
    languageHasChanged ||
    externalIdHasChanged ||
    extraFieldsHasChanged ||
    addressHasChanged;

  const [copyOrgStore] = useMutation<OrgStoreAdminCopyStoreMutation>(graphql`
    mutation OrgStoreAdminCopyStoreMutation($workspaceId: ID!, $storeId: ID!) {
      workspace(id: $workspaceId) {
        copyStoreStructure(storeId: $storeId) {
          storeId
          workspace {
            stores {
              id
            }
          }
        }
      }
    }
  `);

  const [archiveStoreMutation] = useMutation<OrgStoreAdminArchiveStoreMutation>(
    graphql`
      mutation OrgStoreAdminArchiveStoreMutation($storeId: ID!) {
        organisation {
          archiveStore(storeId: $storeId) {
            storeId
          }
        }
      }
    `
  );

  const intl = useIntl();
  const Alert = useAlert();
  function onArchive() {
    Alert.alert(
      intl_tokens.Delete,
      intl.formatMessage(
        {
          defaultMessage: `Are you sure you want to archive "{name}"? Accounts, Employees, Shared Devices and Screens will be deleted. This action cannot be undone.`,
          description: "Org Store Admin Archive Alert",
        },
        {
          name: data.store.name,
        }
      ),
      [
        {
          text: intl_tokens.Cancel,
          style: "cancel",
        },
        {
          text: intl_tokens.Delete,
          style: "destructive",
          onPress: () => {
            archiveStoreMutation({
              variables: { storeId: data.store.id },
              onCompleted: () => {
                props.navigation.goBack();
              },
            });
          },
        },
      ]
    );
  }

  useNavigationHeader({
    type: "SAVE",

    onCancel: props.navigation.goBack,
    title: intl_tokens.StoreAdmin,
    saveLoading: updateInFlight,
    disabled: !isDirty,
    more: [
      {
        label: intl_tokens.Delete,
        icon: "trash",
        color: "negative",
        type: "button",
        onPress: () => {
          onArchive();
        },
      },
      {
        label: intl.formatMessage({
          defaultMessage: "Create new unit from this unit's structure",
          description:
            "Label for creating a new unit from the current uni's structure",
        }),
        icon: "duplicate",
        type: "button",
        onPress: () => {
          if (data.store.workspaceOwner) {
            copyOrgStore({
              variables: {
                storeId: data.store.id,
                workspaceId: data.store.workspaceOwner?.id,
              },
              onCompleted: (ws) => {
                if (ws.workspace.copyStoreStructure) {
                  props.navigation.setParams({
                    storeId: ws.workspace.copyStoreStructure?.storeId,
                  });
                }
              },
            });
          }
        },
      },
    ],

    onSave: () => {
      updateStore({
        variables: {
          storeId: props.route.params.storeId,
          input: {
            setName: nameHasChanged ? (name ?? undefined) : undefined,
            setWorkspaceId: parentWorkspaceIdHasChanged
              ? (parentWorkspaceId ?? undefined)
              : undefined,
            setTimezone: timezoneHasChanged
              ? (timezone ?? undefined)
              : undefined,
            setLocale: languageHasChanged ? (language ?? undefined) : undefined,
            setExternalId: externalIdHasChanged
              ? (externalId ?? undefined)
              : undefined,
            setExtraFields: extraFieldsHasChanged
              ? Object.entries(extraFields)
                  .filter(
                    ([key]) => extraFields[key] !== initialExtraFields[key]
                  )
                  .map(([key, value]) => ({ key, value }))
              : undefined,
            setAddress: addressHasChanged
              ? (selectedPlace?.address ?? undefined)
              : undefined,
            setPlaceId:
              addressHasChanged && selectedPlace?.placeId
                ? selectedPlace.placeId
                : undefined,
          },
        },
      });
    },
  });

  return (
    <Box space="medium" color="surface-background-light" style={{ flex: 1 }}>
      <SectionHeader new>
        <SectionHeader.Title>
          <FormattedMessage
            defaultMessage="Store name"
            description="Store Admin Store Name Label"
          />
        </SectionHeader.Title>
      </SectionHeader>
      <Input
        autoFocus
        color="surface-primary"
        value={name}
        onChangeText={setName}
        space="medium"
        cornerRadius="large"
        placeholder={intl_tokens.Name}
      />
      <Box spaceTop="large" />
      <SectionHeader>
        <SectionHeader.Title>Region</SectionHeader.Title>
      </SectionHeader>
      <Box style={{ flexDirection: "row" }}>
        <TimezoneSelector
          selectedTimezone={timezone}
          onValueChange={setTimezone}
        />
        <Box spaceTop="large" />
        <LanguageSelector
          selectedLanguage={language}
          onValueChange={setLanguage}
        />
      </Box>
      <Box spaceTop="large" />
      <SectionHeader>
        <SectionHeader.Title>External ID</SectionHeader.Title>
      </SectionHeader>
      <Input
        value={externalId}
        onChangeText={setExternalId}
        placeholder="External ID"
      />

      {data.store.extraAdminFields.map((field) => (
        <Box key={field.key}>
          <Box spaceTop="large" />
          <SectionHeader>
            <SectionHeader.Title>{field.label}</SectionHeader.Title>
          </SectionHeader>
          <Input
            value={extraFields[field.key] ?? ""}
            onChangeText={(text) =>
              setExtraFields((prev) => ({ ...prev, [field.key]: text }))
            }
            placeholder={field.description ?? field.label}
          />
        </Box>
      ))}

      <Box spaceTop="large" />
      <SectionHeader>
        <SectionHeader.Title>
          <FormattedMessage
            defaultMessage="Address"
            description="Store Admin Address Label"
          />
        </SectionHeader.Title>
      </SectionHeader>
      <PlaceSearchField
        selectedPlace={selectedPlace}
        onSelectPlace={setSelectedPlace}
      />
      <Box spaceTop="large" />
      <SectionHeader new>
        <SectionHeader.Title>Workspace</SectionHeader.Title>
      </SectionHeader>
      {wId === undefined ? null : (
        <Box spaceHorizontal="small">
          <WorkspaceSelector
            workspaces={data.viewer?.workspaces ?? []}
            onSelectWorkspace={(id) => {
              setParentWorkspaceId(id);
            }}
            mode="large"
            workspaceId={wId}
          />
        </Box>
      )}
      <Box spaceTop="large" />
      <ListItem
        isFree
        border="border-primary"
        right={<ListItem.NavArrow />}
        onPress={() => {
          props.navigation.navigateDeprecated("StoreAdmin", {
            screen: "UserAdmin",
            params: {
              storeId: props.route.params.storeId,
            },
          });
        }}
      >
        <ListItem.Title>{intl_tokens.Users}</ListItem.Title>
      </ListItem>
    </Box>
  );
}

export const OrgStoreAdmin = withWebModalStack(OrgStoreAdminC, true, {
  contentStyle: {
    backgroundColor: "surface-background-light",
  },
});
